import React from 'react';
import { Button, Card, Col, DatePicker, Empty, Form, Input, Row, Space, Statistic, Table, TimePicker, Tooltip, Typography, Avatar } from 'antd';
import { Clock, PlusCircle, ArrowUpCircle, ArrowDownCircle } from 'lucide-react';
import dayjs from 'dayjs';
import { TaskTimeMetrics, TaskTimeLog } from '../../api/KanbanTaskApiClient';
import { Edit } from 'lucide-react';
import { KanbanTask } from '../../stores/KanbanTaskStores';
import { useStore } from '../../stores/root-store-context';

const { Title } = Typography;


const TimeTabContent: React.FC<{
  selectedTask: KanbanTask | null;
  taskTimeMetrics: TaskTimeMetrics | null;
  taskTimeLogs: TaskTimeLog[];
  onLogTime: (values: any) => Promise<void>;
  onUpdateEstimatedTime: (values: any) => Promise<void>;
  timeLoggingForm: any;
  estimatedTimeForm: any;
  isLoggingTime: boolean;
  setIsLoggingTime: (value: boolean) => void;
  isEditingEstimatedTime: boolean;
  setIsEditingEstimatedTime: (value: boolean) => void;
  t: (key: string) => string;
  projectName?: string;
  isReadOnly: boolean;
}> = ({
  selectedTask,
  taskTimeMetrics,
  taskTimeLogs,
  onLogTime,
  onUpdateEstimatedTime,
  timeLoggingForm,
  estimatedTimeForm,
  isLoggingTime,
  setIsLoggingTime,
  isEditingEstimatedTime,
  setIsEditingEstimatedTime,
  t,
  projectName = "current",
  isReadOnly
}) => {
    const {applicationStore} = useStore();
    const formatTime = (time: string) => {
      if (!time) return '00:00';
      if (time.includes(':')) {
        const parts = time.split(':');
        if (parts.length === 2) {
          return `${parts[0]}h ${parts[1]}m`;
        } else if (parts.length === 3) {
          return `${parts[0]}h ${parts[1]}m ${parts[2]}s`;
        }
      }
      return time;
    };

    return (
      <div className="time-tab-content">
        <div className="time-metrics-section">
          <div className="section-header">
            <Title level={5} className="section-title">
              {t('TimeMetrics')}
            </Title>
            {!isEditingEstimatedTime ? (
              <Button
                type="text"
                disabled={isReadOnly}
                icon={<Edit size={14} />}
                onClick={() => {
                  setIsEditingEstimatedTime(true);
                  estimatedTimeForm.setFieldsValue({
                    estimatedTime: taskTimeMetrics?.estimatedTime
                      ? dayjs(taskTimeMetrics.estimatedTime, 'HH:mm')
                      : dayjs('00:00', 'HH:mm')
                  });
                }}
                className="edit-button hover-effect"
              />
            ) : null}
          </div>

          <div className="time-metrics-content">
            {isEditingEstimatedTime ? (
              <Form
                form={estimatedTimeForm}
                layout="vertical"
                initialValues={{
                  estimatedTime: taskTimeMetrics?.estimatedTime
                    ? dayjs(taskTimeMetrics.estimatedTime, 'HH:mm')
                    : dayjs('00:00', 'HH:mm')
                }}
                onFinish={onUpdateEstimatedTime}
                className="estimated-time-form"
              >
                <Form.Item
                  name="estimatedTime"
                  label={t('EstimatedTime')}
                  rules={[
                    { required: true, message: t('PleaseEnterEstimatedTime') }
                  ]}
                >
                  <TimePicker
                    format="HH:mm"
                    className="time-input hover-effect"
                    changeOnBlur={true}
                    allowClear={false}
                    needConfirm={false}
                    hideDisabledOptions={true}
                  />
                </Form.Item>
                <Form.Item>
                  <Space>
                    <Button
                      type="primary"
                      htmlType="submit"
                      className="hover-effect"
                    >
                      {t('Save')}
                    </Button>
                    <Button
                      onClick={() => setIsEditingEstimatedTime(false)}
                      className="hover-effect"
                    >
                      {t('Cancel')}
                    </Button>
                  </Space>
                </Form.Item>
              </Form>
            ) : (
              <div className="metrics-grid">
                <Card className="metric-card">
                  <Statistic
                    title={t('EstimatedTime')}
                    value={formatTime(taskTimeMetrics?.estimatedTime || '00:00')}
                    valueStyle={{ color: '#1890ff' }}
                    prefix={<Clock size={16} />}
                  />
                </Card>
                <Card className="metric-card">
                  <Statistic
                    title={t('TimeSpent')}
                    value={formatTime(taskTimeMetrics?.timeSpent || '00:00')}
                    valueStyle={{ color: taskTimeMetrics?.isOvertime ? '#ff4d4f' : '#52c41a' }}
                    prefix={<Clock size={16} />}
                  />
                </Card>
                <Card className="metric-card">
                  <Statistic
                    title={t('RemainingTime')}
                    value={formatTime(taskTimeMetrics?.remainingTime || '00:00')}
                    valueStyle={{
                      color: taskTimeMetrics?.isOvertime ? '#ff4d4f' : '#52c41a'
                    }}
                    prefix={
                      taskTimeMetrics?.isOvertime
                        ? <ArrowUpCircle size={16} color="#ff4d4f" />
                        : <ArrowDownCircle size={16} color="#52c41a" />
                    }
                  />
                </Card>

              </div>
            )}
          </div>
        </div>

        <div className="log-time-section">
          <div className="section-header">
            <Title level={5} className="section-title">
              {t('LogTime')}
            </Title>
          </div>

          {!isLoggingTime ? (
            <Button
              disabled={isReadOnly}
              type="primary"
              icon={<PlusCircle size={16} />}
              onClick={() => {
                timeLoggingForm.setFieldsValue({
                  time: dayjs('00:00', 'HH:mm'),
                  description: `Working on ${projectName} project.`,
                  endDate: dayjs()
                });
                setIsLoggingTime(true);
              }}
              className="log-time-button hover-effect"
            >
              {t('LogTime')}
            </Button>
          ) : (
            <Form
              form={timeLoggingForm}
              layout="vertical"
              initialValues={{
                time: dayjs('00:00', 'HH:mm'),
                description: `Working on ${projectName} project.`,
                endDate: dayjs()
              }}
              onFinish={onLogTime}
              className="log-time-form"
            >
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    name="time"
                    label={t('Time')}
                    rules={[
                      { required: true, message: t('PleaseEnterTime') }
                    ]}
                  >
                    <TimePicker
                      format="HH:mm"
                      className="time-input hover-effect"
                      changeOnBlur={true}
                      allowClear={false}
                      needConfirm={false}
                      hideDisabledOptions={true}
                    />
                  </Form.Item>
                </Col>
                <Col span={16}>
                  <Form.Item
                    name="endDate"
                    label={t('Date')}
                    rules={[{ required: true, message: t('PleaseSelectDate') }]}
                  >
                    <DatePicker className="date-picker hover-effect" />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item
                name="description"
                label={t('Description')}
                rules={[{ required: true, message: t('PleaseEnterDescription') }]}
              >
                <Input.TextArea
                  rows={3}
                  placeholder={t('DescribeWhatYouWorkedOn')}
                  className="description-input hover-effect"
                />
              </Form.Item>
              <Form.Item>
                <Space>
                  <Button
                    type="primary"
                    htmlType="submit"
                    className="hover-effect"
                  >
                    {t('Save')}
                  </Button>
                  <Button
                    onClick={() => setIsLoggingTime(false)}
                    className="hover-effect"
                  >
                    {t('Cancel')}
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          )}
        </div>

        <div className="time-logs-section">
          <Title level={5} className="section-title" style={{ marginTop: 15, paddingBottom: 10, borderBottom: "1px solid #d9d9d9" }}>
            {t('TimeLogs')}
          </Title>

          {taskTimeLogs.length > 0 ? (
            <Table
              dataSource={taskTimeLogs}
              rowKey="id"
              pagination={false}
              className="time-logs-table"
            >
              <Table.Column
                title={t('User')}
                key="user"
                render={(text, record: TaskTimeLog) => (
                  <div className="user-cell">
                    <Avatar
                      size="small"
                      className="user-avatar"
                      src={
                        record.user?.photoId
                          ? `${applicationStore?.siteConfig?.baseCloudinary}/image/upload/w_300/${record.user.photoId}`
                          : undefined
                      }
                    >
                      {record.user?.firstName?.[0]}
                      {record.user?.lastName?.[0]}
                    </Avatar>
                    <span className="user-name">
                      {record.user?.firstName} {record.user?.lastName}
                    </span>
                  </div>
                )}
              />
              <Table.Column
                title={t('Date')}
                key="date"
                render={(text, record: TaskTimeLog) => (
                  <span>
                    {record.endTime ? dayjs(record.endTime).format('MMM D, YYYY') : '-'}
                  </span>
                )}
              />
              <Table.Column
                title={t('Time')}
                dataIndex="totalTime"
                key="totalTime"
                render={(time) => <span>{formatTime(time)}</span>}
              />
              <Table.Column
                title={t('Description')}
                dataIndex="description"
                key="description"
                ellipsis={{ showTitle: false }}
                render={(description) => (
                  <Tooltip title={description}>
                    <span>{description}</span>
                  </Tooltip>
                )}
              />
            </Table>
          ) : (
            <Empty description={t('NoTimeLogsYet')} className="no-logs" style={{ padding: 35 }} />
          )}
        </div>
      </div>
    );
  };

export default TimeTabContent;